// SUB-OFFICE 7 — boot. The Bureau does not say the word for what it is doing
// while the tube stutters; the black cover in index.html IS the first frame.
// Order: WebGL detect -> collision bake -> world build -> WARMED tier probe
// (compile, 60 discarded frames, THEN 120 measured — boot jank can no longer
// misclassify a capable machine) -> the hall assembles itself in the light.

import { EventBus, EntityRegistry } from './entities/entities';
import { SceneManager, guessTier, bureauUniforms, type ViewMode } from './render/scene';
import { FirstPersonView } from './render/fpview';
import { printLayer } from './ui/chrome';
import { PostPipeline } from './render/post';
import { buildWorld } from './render/world';
import { bakeCollision, collisionStats } from './world/collision';
import { ArrivalController } from './world/zones/arrival';
import { VerticalCoreController } from './world/zones/verticalcontroller';
import { Player } from './entities/player';
import { EyeLamps } from './entities/lamps';
import { ClerkWindows } from './entities/windows';
import { Props } from './entities/props';
import { Canisters } from './entities/canisters';
import { Ghosts } from './entities/ghosts';
import { BlobShadows } from './render/shadows';
import { FpHands } from './render/fphands';
import { InputManager } from './input/input';
import { DomUi } from './ui/dom';
import { BureauAudio } from './audio';
import { Fsm } from './state';
import { startStatsPoll } from './net';
import { Game } from './game';
import { dispatchRunner } from './physical/bench';
import { generateSpec, type PhysicalSystemId } from '../shared/physspec';
import { validateEnvelope } from '../shared/physlog';
import type { PerfTier } from '../shared/types';

export function bootBureau(): void {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const app = document.getElementById('app') as HTMLElement;
  const bootCover = document.getElementById('boot') as HTMLElement;
  const noWindow = document.getElementById('nowindow') as HTMLElement;

  // ---- WebGL detect: no window onto the hall -> paper correspondence ------
  let sceneMgr: SceneManager;
  try {
    const probe = document.createElement('canvas');
    if (!probe.getContext('webgl2') && !probe.getContext('webgl')) throw new Error('no webgl');
    sceneMgr = new SceneManager(app);
  } catch {
    bootCover.remove();
    noWindow.classList.add('shown');
    return; // paper.ts (M12) replaces this notice with the full paper flow
  }

  const bus = new EventBus();
  const dom = new DomUi(bus, reduced);
  const fsm = new Fsm(bus);
  const audio = new BureauAudio(bus);
  sceneMgr.setTier(guessTier());

  // ---- the building -------------------------------------------------------
  bakeCollision();
  const world = buildWorld();
  sceneMgr.scene.add(world.group);

  const registry = new EntityRegistry();
  const player = new Player(bus);
  const lamps = new EyeLamps(bus, player);
  const windows = new ClerkWindows(bus, world, reduced);
  const props = new Props(bus, world, player, reduced);
  const canisters = new Canisters(bus);
  const ghosts = new Ghosts(bus, player);
  const shadows = new BlobShadows(player, ghosts);
  registry.add(player);
  registry.add(shadows);
  registry.add(lamps);
  registry.add(windows);
  registry.add(props);
  registry.add(canisters);
  registry.add(ghosts);
  sceneMgr.scene.add(
    player.group, lamps.heads, lamps.lenses, windows.group, props.group,
    canisters.group, ghosts.group, shadows.mesh
  );
  // GRAPHICS R1 (#75): the walking bodies cast real shadows on tier A/B. The
  // blob discs (shadows.mesh) stay for tier C where the shadow map is disabled.
  for (const g of [player.group, ghosts.group]) {
    g.traverse((o) => {
      const m = o as unknown as { isMesh?: boolean; castShadow?: boolean };
      if (m.isMesh) m.castShadow = true;
    });
  }

  const input = new InputManager(bus, fsm, player, sceneMgr.camera, dom);

  // ---- THE VANTAGE: first-person is the only player view -------------------
  // The top-down rig (scene.ts TOP mode) is retained as an INTERNAL cinematic
  // tool for the pre-designation intake sequence, ceremony cutaways, and
  // marketing capture — but it is no longer player-switchable. Intake plays in
  // TOP (the long establishing lens); designation drops the player into FP for
  // good. There is no V key and no stored player preference.
  const fpv = new FirstPersonView(sceneMgr.renderer.domElement, reduced);
  sceneMgr.fp = fpv;
  input.fpYaw = () => (sceneMgr.view === 'FP' ? fpv.yaw : null);

  // FP hands: camera-space presence anchor (the camera joins the scene graph
  // so its children render; this changes nothing else about the rig)
  sceneMgr.scene.add(sceneMgr.camera);
  const fpHands = new FpHands(bus, player, fsm, fpv, () => sceneMgr.view === 'FP', reduced);
  sceneMgr.camera.add(fpHands.group);
  registry.add(fpHands);
  const applyView = (v: ViewMode): void => {
    if (v === sceneMgr.view) return;
    if (v === 'FP') fpv.syncFromFacing(player.heading);
    sceneMgr.setView(v);
    world.ceiling.visible = v === 'FP';
    player.setFirstPerson(v === 'FP');
    world.glowMat.opacity = v === 'FP' ? 0.15 : 0.09; // lamp pools press closer at eye level
  };
  // Designation complete → enter FP once, permanently, for every file (new or
  // returning). The intake sequence up to this point stays in the TOP lens.
  bus.on('fp:unlock', () => applyView('FP'));
  // SAFETY NET (the V-key toggle no longer exists — fp:unlock is the ONLY door
  // into FP, so a single missed emit strands the player in TOP forever). Every
  // DESIGNATED gameplay state is first-person by policy; the only TOP states
  // are the pre-designation intake cinematic (INTAKE_FIRST → ADJUDICATION →
  // VERDICT → ACK, which own the long establishing lens until the designation
  // ceremony fires its explicit fp:unlock). So: any transition INTO a post-
  // designation walking state re-asserts FP. applyView is idempotent, so this
  // is a no-op when FP is already engaged; it self-heals the recovery path
  // (filed-while-you-waited → openSession → ROAM, which never emitted unlock),
  // returning-file boot, and any timing race on the two explicit emits.
  bus.on('fsm', (p) => {
    const to = (p as { to?: string }).to;
    if (to === 'ROAM' || to === 'RETURN_GATE' || to === 'ABSENCE_ACCOUNTING' || to === 'ARRIVAL_EXTERIOR' ||
        to === 'LIFT_LOBBY' || to === 'DESCENT_STUB') applyView('FP');
  });
  // Internal cinematic hook (retained for intake/ceremony cutaways + capture;
  // NOT a player-facing toggle — there is no menu path bound to it in-game).
  (window as unknown as Record<string, unknown>).bureauView = {
    set: (v: ViewMode): void => { if (v === 'TOP' || v === 'FP') applyView(v); },
    get: (): ViewMode => sceneMgr.view,
  };
  const overlayRoot = printLayer();

  // ---- post pipeline (TIER A only) ----------------------------------------
  const post = new PostPipeline();

  // ---- the gameplay director (M6-M9): session, docket, ceremonies ---------
  const game = new Game(bus, fsm, sceneMgr, post, player, lamps, canisters, dom, audio);
  registry.add(game);

  // ---- THE ARRIVAL EXTERIOR (M-A): the boot-only walk-in ------------------
  // The lot is one visible zone at a time: hide the resident interior while the
  // exterior is up (the player group stays — it is the avatar). buildWorld has
  // no dispose path, so visibility-toggling is the low-risk streaming route for
  // two zones; full build/dispose streaming lands with the elevator in M-B.
  const setInteriorVisible = (on: boolean): void => {
    world.group.visible = on; // ceiling is a child of world.group
    lamps.heads.visible = on;
    lamps.lenses.visible = on;
    windows.group.visible = on;
    props.group.visible = on;
    canisters.group.visible = on;
    ghosts.group.visible = on;
    shadows.mesh.visible = on;
  };
  const arrival = new ArrivalController({
    scene: sceneMgr.scene, sceneMgr, player, fpv, fsm, dom, bus,
    applyView, setInteriorVisible, reducedMotion: reduced,
  });
  game.setArrival(arrival);

  // ---- THE VERTICAL CIRCULATION CORE (M-B): the lift lobby + cab spine ----
  // The N-zone streaming spine (ZoneManager) that carries every future zone.
  // Entered from Intake via the working lift door; the cab masks the streamed
  // build/dispose of the descent-vestibule stub (the M-D placeholder).
  const vertcore = new VerticalCoreController({
    scene: sceneMgr.scene, sceneMgr, player, fpv, fsm, dom, bus,
    applyView, setInteriorVisible, reducedMotion: reduced,
  });
  game.setVerticalCore(vertcore);
  const syncPost = (): void => {
    post.enabled = sceneMgr.tier === 'A';
    post.setSize(window.innerWidth, window.innerHeight, sceneMgr.renderer.getPixelRatio());
  };
  syncPost();
  window.addEventListener('resize', syncPost);
  sceneMgr.onTierChange = (t: PerfTier) => {
    syncPost();
    if (__DEV__) console.log('[tier]', t);
  };

  startStatsPoll(bus);

  // dev handle for scripted verification (never present in production builds)
  if (__DEV__) {
    // __DEV__-only bench-drive harness: render any physical runner in FP with a
    // locally-generated (seed-deterministic) spec and return its raw log +
    // envelope validity — the same structural gate the worker applies. Lets QA
    // exercise grade-gated W2 systems (8-R/5-M/4-T/…) without grinding standing.
    const benchDrive = async (system: string, gradeTier = 1): Promise<Record<string, unknown>> => {
      const spec = generateSpec(system as PhysicalSystemId, `dev-${system}-${Date.now()}`, gradeTier);
      const p = dispatchRunner({ bus, player, sceneMgr, fsm }, `dev-${system}`, system, spec);
      if (p === undefined) return { produced: false, reason: 'unknown system' };
      const log = await p;
      if (!log) return { produced: false, reason: 'abandoned' };
      const check = validateEnvelope(log, { system, issuedAt: log.t0, now: Date.now() });
      return { produced: true, valid: check.ok, reason: check.reason, events: log.events.length, hasRoute: !!log.route, spec };
    };
    (window as unknown as Record<string, unknown>).__bureau = {
      scene: sceneMgr.scene, camera: sceneMgr.camera, renderer: sceneMgr.renderer,
      player, props, world, bus, fsm, game, canisters, post, fpv, sceneMgr, ghosts,
      benchDrive, bureauUniforms,
    };
  }

  // ---- debug hotkey (backtick): renderer.info budgets ---------------------
  let fps = 0;
  bus.on('debug', () => {
    if (!__DEV__) return; // production console stays silent even on the hotkey
    const info = sceneMgr.info();
    console.log(
      '[SUB-OFFICE 7 debug]\n' +
        `  draw calls : ${info.calls}   (budget <= 80, expected ~30-45)\n` +
        `  triangles  : ${info.triangles}   (budget <= 60000, expected ~35-50k)\n` +
        `  programs   : ${info.programs}\n` +
        `  fps        : ${fps.toFixed(1)}\n` +
        `  tier       : ${sceneMgr.tier}  post: ${post.enabled}\n` +
        `  world build: ${world.buildMs.toFixed(1)}ms  static tris: ${world.tris}\n` +
        `  collision  : ${JSON.stringify(collisionStats())}`
    );
  });

  // ---- WARMED tier probe: compile everything, discard 60, measure 120 -----
  sceneMgr.renderer.compile(sceneMgr.scene, sceneMgr.camera);
  let probeFrame = 0;
  let probeAccum = 0;
  let probeDone = false;

  // ---- main loop ----------------------------------------------------------
  let last = performance.now();
  let lastFrameMs = 8;
  let rafId = 0;
  let revealed = false;

  const frame = (now: number): void => {
    rafId = requestAnimationFrame(frame);
    const dt = Math.min(0.033, (now - last) / 1000);
    last = now;
    fps = fps * 0.95 + (dt > 0 ? 1 / dt : 60) * 0.05;

    const t0 = performance.now();
    // FP pointer lock: overlays and dead-movement states release it; the
    // canvas click reacquires. The crosshair follows the same truth.
    fpv.setWantLock(fsm.movementAllowed() && overlayRoot.childElementCount === 0);
    input.update();
    registry.update(dt);
    dom.update(dt);
    sceneMgr.follow(player.pos, player.vel, dt);
    sceneMgr.update(dt, lastFrameMs);
    // Appeals tungsten: the wood room warms only from the eye, only when stood
    // inside its footprint (x[-24,-18] z[6,17]) — the long lens stays neutral.
    const inAppeals =
      sceneMgr.view === 'FP' &&
      player.pos.x >= -24 && player.pos.x <= -18 &&
      player.pos.z >= 6 && player.pos.z <= 17;
    post.setWarm(inAppeals ? 1 : 0);
    post.render(sceneMgr.renderer, sceneMgr.scene, sceneMgr.camera, dt);
    lastFrameMs = performance.now() - t0;

    // tier probe bookkeeping (frames 60..180 measured)
    if (!probeDone) {
      probeFrame++;
      if (probeFrame > 60) probeAccum += lastFrameMs;
      if (probeFrame >= 180) {
        probeDone = true;
        const avg = probeAccum / 120;
        const measured: PerfTier = avg < 8 ? 'A' : avg < 16 ? 'B' : 'C';
        sceneMgr.setTier(measured);
        if (__DEV__) console.log(`[probe] avg ${avg.toFixed(2)}ms -> tier ${measured}`);
      }
    }

    // the tube catches: reveal after the first few real frames
    if (!revealed && probeFrame > 3) {
      revealed = true;
      bootCover.classList.add('reveal');
      window.setTimeout(() => bootCover.remove(), reduced ? 700 : 700);
      void game.enterWorld(); // INTAKE_FIRST, RETURN_GATE, or ABSENCE_ACCOUNTING
    }
  };
  rafId = requestAnimationFrame(frame);

  // tab hidden: the hall pauses (rAF, polls, audio all sleep)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      cancelAnimationFrame(rafId);
    } else {
      last = performance.now();
      rafId = requestAnimationFrame(frame);
    }
  });

  // WebGL context loss: rebuild masked as a power flicker (full path in M12)
  sceneMgr.renderer.domElement.addEventListener('webglcontextlost', (e: Event) => {
    e.preventDefault();
  });
}

declare const __DEV__: boolean;
